import React, { forwardRef } from 'react';

import { Icon } from '../Icon/Icon';
import type {
  IconButtonAsAnchorProps,
  IconButtonAsButtonProps,
  IconButtonProps,
} from './IconButton.types';
import * as styles from './IconButton.styles';

interface IconButtonOverloads {
  (
    props: IconButtonAsButtonProps & React.RefAttributes<HTMLButtonElement>,
  ): React.ReactElement | null;
  (
    props: IconButtonAsAnchorProps & React.RefAttributes<HTMLAnchorElement>,
  ): React.ReactElement | null;
  displayName?: string;
}

function isAnchorProps(props: IconButtonProps): props is IconButtonAsAnchorProps {
  return (props as IconButtonAsAnchorProps).href !== undefined;
}

export const IconButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, IconButtonProps>(
  function IconButton({ compact = false, icon, ...props }, ref) {
    const { as, ...anchorProps } = props as IconButtonAsAnchorProps;
    const iconSize = compact ? 'sm' : 'md';
    const buttonStyles = [styles.root, styles.appearance, compact ? styles.compact : undefined];
    const content = (
      <span css={styles.icon}>
        <Icon aria-hidden name={icon} size={iconSize} />
      </span>
    );

    if (isAnchorProps({ compact, icon, ...props } as IconButtonProps)) {
      if (as && as !== 'a') {
        return React.createElement(
          as as React.ElementType,
          {
            ...anchorProps,
            css: buttonStyles,
            ref: ref as React.Ref<unknown>,
          },
          content,
        );
      }

      return (
        <a
          {...(anchorProps as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
          css={buttonStyles}
          ref={ref as React.Ref<HTMLAnchorElement>}
        >
          {content}
        </a>
      );
    }

    const buttonProps = props as React.ButtonHTMLAttributes<HTMLButtonElement>;

    return (
      <button
        {...buttonProps}
        css={buttonStyles}
        ref={ref as React.Ref<HTMLButtonElement>}
        type={buttonProps.type ?? 'button'}
      >
        {content}
      </button>
    );
  },
) as unknown as IconButtonOverloads;

IconButton.displayName = 'IconButton';
